import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { ErrorLike } from '@apollo/client';
import _get from 'lodash/get';
import {
  FilterGroupingEnum,
  WidgetTemplate,
  WidgetTemplatesKindEnum,
  WidgetTemplatesVisibilityEnum,
  useWidgetTemplatesWithCountersLazyQuery,
  WidgetTemplateFilterInputObject,
  SortDirectionEnum,
} from 'graphql-common';
import useSearchParam from '@lib/hooks/useSearchParam';
import useTabParam from '@lib/hooks/useTabParam';
import { CREATABLE_WIDGET_TEMPLATE_KINDS } from '../constants';

const PAGE_SIZE = 20;

/** Visibility filter options */
export type VisibilityFilter = 'all' | 'public' | 'private';

/** Counts for visibility tabs */
export interface TemplateCounts {
  all: number;
  public: number;
  private: number;
}

/** Return type for useWidgetTemplatesList hook */
interface UseWidgetTemplatesListResult {
  // Templates data
  templates: WidgetTemplate[];
  counts: TemplateCounts;

  // Loading states
  loading: boolean;
  hasMore: boolean;

  // Error state
  error: ErrorLike | undefined;

  // Filters
  visibilityFilter: VisibilityFilter;
  setVisibilityFilter: (filter: VisibilityFilter) => void;
  kindFilter: WidgetTemplatesKindEnum | null;
  setKindFilter: (kind: WidgetTemplatesKindEnum | null) => void;

  // Pagination
  loadMore: () => void;
  refetch: () => void;

  // Local state update (for optimistic updates)
  updateTemplateInList: (updatedTemplate: WidgetTemplate) => void;
}

/**
 * Hook for managing widget templates list data
 * Handles fetching, filtering, and pagination of templates for the sidebar
 */
export default function useWidgetTemplatesList(): UseWidgetTemplatesListResult {
  // Filter state
  const [searchQuery] = useSearchParam({ usePagination: false });
  const [tabParam, setTabParam] = useTabParam({
    defaultTab: 'all',
    usePagination: false,
  });
  const visibilityFilter = (tabParam || 'all') as VisibilityFilter;
  const setVisibilityFilter = (filter: VisibilityFilter) => setTabParam(filter);
  const [kindFilter, setKindFilter] = useState<WidgetTemplatesKindEnum | null>(
    null,
  );

  // Pagination state
  const [templates, setTemplates] = useState<WidgetTemplate[]>([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<ErrorLike | undefined>(undefined);

  // Counts from server
  const [counts, setCounts] = useState<TemplateCounts>({
    all: 0,
    public: 0,
    private: 0,
  });

  // Refs to prevent stale requests
  const fetchIdRef = useRef(0);
  const isFetchingRef = useRef(false);

  // Lazy query for templates list with counters
  const [fetchTemplatesLazy] = useWidgetTemplatesWithCountersLazyQuery({
    fetchPolicy: 'network-only',
  });

  // Build base filters for query (kind + search) - without visibility
  const buildBaseFilters =
    useCallback((): WidgetTemplateFilterInputObject[] => {
      const result: WidgetTemplateFilterInputObject[] = [
        {
          isDefault: {
            grouping: FilterGroupingEnum.And,
            predicate: { false: true },
          },
        },
      ];

      if (kindFilter) {
        result.push({
          kind: {
            grouping: FilterGroupingEnum.And,
            predicate: { eq: kindFilter },
          },
        });
      } else {
        result.push({
          kind: {
            grouping: FilterGroupingEnum.And,
            predicate: { in: [...CREATABLE_WIDGET_TEMPLATE_KINDS] },
          },
        });
      }

      if (searchQuery) {
        result.push({
          title: {
            grouping: FilterGroupingEnum.Or,
            predicate: { cont: searchQuery },
          },
        });
      }

      return result;
    }, [kindFilter, searchQuery]);

  // Build filters with visibility for main query
  const buildFiltersWithVisibility =
    useCallback((): WidgetTemplateFilterInputObject[] => {
      const baseFilters = buildBaseFilters();

      if (visibilityFilter !== 'all') {
        const visibility =
          visibilityFilter === 'public'
            ? WidgetTemplatesVisibilityEnum.Pub
            : WidgetTemplatesVisibilityEnum.Prv;
        baseFilters.push({
          visibility: {
            grouping: FilterGroupingEnum.And,
            predicate: { eq: visibility },
          },
        });
      }

      return baseFilters;
    }, [buildBaseFilters, visibilityFilter]);

  // Build public templates filters for counter
  const buildPublicFilters =
    useCallback((): WidgetTemplateFilterInputObject[] => {
      const baseFilters = buildBaseFilters();
      baseFilters.push({
        visibility: {
          grouping: FilterGroupingEnum.And,
          predicate: { eq: WidgetTemplatesVisibilityEnum.Pub },
        },
      });
      return baseFilters;
    }, [buildBaseFilters]);

  // Build private templates filters for counter
  const buildPrivateFilters =
    useCallback((): WidgetTemplateFilterInputObject[] => {
      const baseFilters = buildBaseFilters();
      baseFilters.push({
        visibility: {
          grouping: FilterGroupingEnum.And,
          predicate: { eq: WidgetTemplatesVisibilityEnum.Prv },
        },
      });
      return baseFilters;
    }, [buildBaseFilters]);

  // Load page of templates
  const loadPage = useCallback(
    async (nextPage: number) => {
      const isReset = nextPage === 1;
      if (isFetchingRef.current && !isReset) {
        return;
      }

      fetchIdRef.current += 1;
      const fetchId = fetchIdRef.current;

      if (isReset) {
        setPage(0);
        setTotalPages(0);
      }

      isFetchingRef.current = true;
      setIsFetching(true);
      setError(undefined);

      const filters = buildFiltersWithVisibility();
      const allTemplatesFilters = buildBaseFilters();
      const publicTemplatesFilters = buildPublicFilters();
      const privateTemplatesFilters = buildPrivateFilters();

      try {
        const res = await fetchTemplatesLazy({
          variables: {
            page: nextPage,
            limit: PAGE_SIZE,
            sorts: { title: SortDirectionEnum.Asc },
            filters,
            allTemplatesFilters,
            publicTemplatesFilters,
            privateTemplatesFilters,
          },
        });

        if (fetchId !== fetchIdRef.current) {
          return;
        }

        if (res.error) {
          setError(res.error);
          return;
        }

        const collection =
          (_get(res, 'data.data.collection') as WidgetTemplate[]) || [];
        const metadata = res.data?.data?.metadata;

        if (isReset) {
          setTemplates(collection);
        } else {
          setTemplates((prev) => [...prev, ...collection]);
        }

        if (metadata) {
          setPage(metadata.currentPage || nextPage);
          setTotalPages(metadata.totalPages || 0);
        } else {
          setPage(nextPage);
        }

        const allCount = res.data?.allTemplates?.collection?.length ?? 0;
        const publicCount = res.data?.publicTemplates?.collection?.length ?? 0;
        const privateCount =
          res.data?.privateTemplates?.collection?.length ?? 0;
        setCounts({
          all: allCount,
          public: publicCount,
          private: privateCount,
        });
      } catch (e) {
        if (fetchId === fetchIdRef.current) {
          setError(e as ErrorLike);
        }
      } finally {
        if (fetchId === fetchIdRef.current) {
          isFetchingRef.current = false;
          setIsFetching(false);
        }
      }
    },
    [
      fetchTemplatesLazy,
      buildFiltersWithVisibility,
      buildBaseFilters,
      buildPublicFilters,
      buildPrivateFilters,
    ],
  );

  // Load more templates
  const loadMore = useCallback(() => {
    if (page < totalPages && !isFetchingRef.current) {
      loadPage(page + 1);
    }
  }, [page, totalPages, loadPage]);

  // Refetch templates from page 1
  const refetch = useCallback(() => {
    loadPage(1);
  }, [loadPage]);

  // Fetch templates when filters change
  useEffect(() => {
    setTemplates([]);
    loadPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kindFilter, searchQuery, visibilityFilter]);

  const hasMore = useMemo(() => page < totalPages, [page, totalPages]);

  // Update a template in local state without refetching
  const updateTemplateInList = useCallback(
    (updatedTemplate: WidgetTemplate) => {
      setTemplates((prev) =>
        prev.map((t) => (t.id === updatedTemplate.id ? updatedTemplate : t)),
      );
    },
    [],
  );

  return {
    templates,
    counts,
    loading: isFetching,
    hasMore,
    error,
    visibilityFilter,
    setVisibilityFilter,
    kindFilter,
    setKindFilter,
    loadMore,
    refetch,
    updateTemplateInList,
  };
}
